<script setup lang="ts">
import { ref, shallowRef } from "vue";
import GeneratedPersonSearchTable from "./GeneratedPersonSearchTable.vue";
import PersonLink from "@/components/shared/PersonLink.vue";
import { usePersonProfileAction } from "@/composables/people/usePersonProfileAction";
import { usePersonService } from "../../../cbi-generator/frontend/composables/usePersonFrontendService";
import { emptySearchParams } from "@/composables/useTableHelper";
import { useUtility } from "@/composables/useUtility";
import type { GeneratedTableConfig } from "@/composables/generated/contracts";
import type { SignoutStaff } from "../../../cbi-generator/generated/dto/SignoutStaff";
import type { SignoutStaffSearchParams } from "../../../cbi-generator/generated/dto/SignoutStaffSearchParams";
import type { SignoutStaffSearchResult } from "../../../cbi-generator/generated/dto/SignoutStaffSearchResult";
import { SignoutStaffSearchParamsMeta } from "../../../cbi-generator/generated/dto/SignoutStaffSearchParamsMeta";
import { SignoutStaffSearchResultMeta } from "../../../cbi-generator/generated/dto/SignoutStaffSearchResultMeta";
import type { SignoutMemberSearchParams } from "../../../cbi-generator/generated/dto/SignoutMemberSearchParams";
import type { SignoutMemberSearchResult } from "../../../cbi-generator/generated/dto/SignoutMemberSearchResult";
import { SignoutMemberSearchParamsMeta } from "../../../cbi-generator/generated/dto/SignoutMemberSearchParamsMeta";
import { SignoutMemberSearchResultMeta } from "../../../cbi-generator/generated/dto/SignoutMemberSearchResultMeta";
import type { SignoutCrewStaffSearchParams } from "../../../cbi-generator/generated/dto/SignoutCrewStaffSearchParams";
import type { SignoutCrewStaffSearchResult } from "../../../cbi-generator/generated/dto/SignoutCrewStaffSearchResult";
import { SignoutCrewStaffSearchParamsMeta } from "../../../cbi-generator/generated/dto/SignoutCrewStaffSearchParamsMeta";
import signoutTypes from "@/api/mock/signOutTypes.json";

const props = withDefaults(
  defineProps<{ personId: number; mode?: "staff" | "member" }>(),
  { mode: "staff" },
);
type Row = SignoutStaffSearchResult | SignoutMemberSearchResult;
const isMember = props.mode === "member";
const service = usePersonService();
const { formatDateNY, formatTimeNY } = useUtility();
const { saving, error, clear, save } = usePersonProfileAction();
const dialog = ref(false);
const selectedId = ref<number | null>(null);
const selected = shallowRef<SignoutStaff | null>(null);
const crew = shallowRef<SignoutCrewStaffSearchResult[]>([]);
function datetime(date: Date | null) {
  return date ? `${formatDateNY(date)} ${formatTimeNY(date)}` : "--";
}
function typeName(code: string) {
  const names: Record<string, string> = signoutTypes;
  return names[code] ?? code;
}
const staffConfig: GeneratedTableConfig<
  SignoutStaffSearchResult,
  SignoutStaffSearchParams
> = {
  id: `person-${props.personId}-signouts`,
  rowKey: "signoutId",
  resultMeta: SignoutStaffSearchResultMeta,
  searchMeta: SignoutStaffSearchParamsMeta,
  initialFilter: () => ({
    ...emptySearchParams<SignoutStaffSearchParams>(
      SignoutStaffSearchParamsMeta,
    ),
    personIdExact: props.personId,
    sort: [],
  }),
  search: (params, page, perPage) =>
    service.searchSignoutStaff(
      { ...params, personIdExact: props.personId },
      page,
      perPage,
    ),
  visibleColumns: [
    "signoutId",
    "programName",
    "role",
    "boatName",
    "signoutType",
    "signoutDatetime",
    "actions",
  ],
  defaultSort: [{ key: "signoutDatetime", order: "desc" }],
};
// Member search is scoped server-side to signouts where a household member was skipper or crew.
// The detail dialog (crew lookup, comments, staff person links) stays staff-only.
const memberConfig: GeneratedTableConfig<
  SignoutMemberSearchResult,
  SignoutMemberSearchParams
> = {
  ...staffConfig,
  id: `person-${props.personId}-signouts-member`,
  rowKey: "signoutId",
  resultMeta: SignoutMemberSearchResultMeta,
  searchMeta: SignoutMemberSearchParamsMeta,
  initialFilter: () => ({
    ...emptySearchParams<SignoutMemberSearchParams>(
      SignoutMemberSearchParamsMeta,
    ),
    personIdExact: props.personId,
    sort: [],
  }),
  search: (params, page, perPage) =>
    service.searchSignoutMember(
      { ...params, personIdExact: props.personId },
      page,
      perPage,
    ),
  visibleColumns: [
    "programName",
    "role",
    "boatName",
    "sailNumber",
    "signoutType",
    "signoutDatetime",
    "signinDatetime",
  ],
};
const config = (isMember ? memberConfig : staffConfig) as GeneratedTableConfig<
  Row,
  SignoutMemberSearchParams
>;
const headers = [
  {
    key: "role",
    title: "Role",
    sortable: false,
    computeValue: (row: Row) =>
      row.skipperId === props.personId ? "Skipper" : "Crew",
  },
  {
    key: "signoutType",
    title: "Type",
    computeValue: (row: Row) => typeName(row.signoutType),
  },
  {
    key: "signoutDatetime",
    title: "Sign Out",
    computeValue: (row: Row) => datetime(row.signoutDatetime),
  },
  ...(isMember
    ? [
        {
          key: "signinDatetime",
          title: "Sign In",
          computeValue: (row: Row) => datetime(row.signinDatetime),
        },
      ]
    : [{ key: "actions", title: "Actions", sortable: false }]),
];
async function loadDetail() {
  const id = selectedId.value;
  if (id == null) return;
  await save(async () => {
    const detail = await service.getSignoutStaff(id, {
      personId: true,
      programId: true,
      boatId: true,
      testRatingId: true,
    });
    const people: SignoutCrewStaffSearchResult[] = [];
    let page = 1;
    let totalPages = 1;
    do {
      const result = await service.searchSignoutCrewStaff(
        {
          ...emptySearchParams<SignoutCrewStaffSearchParams>(
            SignoutCrewStaffSearchParamsMeta,
          ),
          signoutIdExact: id,
          sort: [],
        },
        page,
        100,
      );
      people.push(...result.items);
      totalPages = result.totalPages;
      page++;
    } while (page <= totalPages);
    selected.value = detail;
    crew.value = people;
  });
}
async function open(row: Row) {
  clear();
  selected.value = null;
  crew.value = [];
  selectedId.value = row.signoutId;
  dialog.value = true;
  await loadDetail();
}
const actionsSlot = "item.actions";
</script>

<template>
  <v-card title="Signouts" elevation="0" class="pa-6">
    <GeneratedPersonSearchTable
      :config="config"
      :hide-export="isMember"
      :headers="headers"
      :filter-keys="[
        'signoutDatetimeGreaterEq',
        'signoutDatetimeLessEq',
        'sailNumberLike',
        'signoutTypeExact',
      ]"
    >
      <template v-if="!isMember" v-slot:[actionsSlot]="{ item }"
        ><v-btn variant="text" size="small" @click="open(item)"
          >View</v-btn
        ></template
      >
    </GeneratedPersonSearchTable>
    <v-dialog
      v-if="!isMember"
      v-model="dialog"
      max-width="900"
      :persistent="saving"
    >
      <v-card :title="`Signout (${selectedId})`" :loading="saving">
        <v-card-text>
          <v-alert v-if="error" type="error" role="alert" class="mb-4"
            >{{ error }}
            <v-btn :disabled="saving" @click="loadDetail"
              >Retry signout</v-btn
            ></v-alert
          >
          <DescriptionList v-if="selected">
            <DescriptionListItem term="Program">{{
              selected.programId?.programName ?? "--"
            }}</DescriptionListItem>
            <DescriptionListItem term="Type">{{
              typeName(selected.signoutType)
            }}</DescriptionListItem>
            <DescriptionListItem term="Boat">{{
              selected.boatId?.boatName ?? "--"
            }}</DescriptionListItem>
            <DescriptionListItem term="Sail #">{{
              selected.sailNumber ?? "--"
            }}</DescriptionListItem>
            <DescriptionListItem term="Hull #">{{
              selected.hullNumber ?? "--"
            }}</DescriptionListItem>
            <DescriptionListItem term="Role">{{
              selected.personId?.personId === personId ? "Skipper" : "Crew"
            }}</DescriptionListItem>
            <DescriptionListItem term="Sign Out">{{
              datetime(selected.signoutDatetime)
            }}</DescriptionListItem>
            <DescriptionListItem term="Sign In">{{
              datetime(selected.signinDatetime)
            }}</DescriptionListItem>
            <DescriptionListItem term="Skipper"
              ><PersonLink
                :person-id="selected.personId?.personId"
                :name="selected.personId?.name"
            /></DescriptionListItem>
            <DescriptionListItem term="Crew">
              <p v-for="member in crew" :key="member.crewId">
                <PersonLink
                  :person-id="member.personIdValue ?? undefined"
                  :name="member.personName ?? undefined"
                /></p
            ></DescriptionListItem>
            <DescriptionListItem term="Test Rating">{{
              selected.testRatingId?.ratingName ?? "--"
            }}</DescriptionListItem>
            <DescriptionListItem term="Test Results">{{
              selected.testResult ?? "--"
            }}</DescriptionListItem>
            <DescriptionListItem term="Comments">{{
              selected.comments ?? "--"
            }}</DescriptionListItem>
          </DescriptionList>
        </v-card-text>
        <v-card-actions
          ><v-btn :disabled="saving" @click="dialog = false"
            >Close</v-btn
          ></v-card-actions
        >
      </v-card>
    </v-dialog>
  </v-card>
</template>
